<template>
    <div ref="tableRef" class="schedule-keyboard-surface" tabindex="0" @keydown="handleKeyDown">
        <div v-if="scheduleStore.loading" class="flex justify-center h-[70vh] items-center">
            <Loader />
        </div>
        <template v-else-if="dates">
            <div v-if="!scheduleStore.isScheduleContextLoaded" class="ds-week-table-message">
                Не удалось загрузить график за {{ layout === 'month' ? 'выбранный месяц' : 'выбранную неделю' }}. Повторите выбор периода.
            </div>
            <div v-else-if="!schedules.length" class="ds-week-table-message">
                Ничего не найдено
            </div>
            <div v-else v-for="schedule in schedules" :key="schedule.id" class="ds-week-row">
                <div class="ds-week-lead-cell">
                    <!-- Как в месяце: вакансия — голубая, аутсорс — жёлтая; полоса предупреждения сотрудника рисуется поверх. -->
                    <div class="absolute left-0 top-0 h-full w-[4px]" :class="{
                        'bg-lightblue': schedule.is_vacancy && !schedule.is_outsource,
                        'bg-yellow': schedule.is_vacancy && schedule.is_outsource
                    }"></div>
                    <Employee :schedule layout="week" :position="schedule.position?.name" class="cursor-pointer"
                        @click="emit('user-clicked', schedule)"
                        :warned="isWarnedEmployee(schedule.id)" :hours-limit-violation="getHoursLimitViolation(schedule.id)" />
                </div>
                <div class="ds-week-days">
                    <template v-if="layout === 'month'">
                        <MonthScheduleItem v-for="date in dates" :key="date" :schedule :date class="no-select"
                            :data-copy-cell="copyCellKey(schedule.id, date)"
                            :class="[copyHighlightClass(schedule.id, date), {
                                '!bg-yellow': isSourceCell(schedule, date),
                                '!bg-lightred': isTargetCell(schedule, date),
                            }]"
                            @click="onDayClick($event, date, schedule)" />
                    </template>
                    <template v-else>
                        <WeekScheduleItem v-for="date in dates" :key="date" :schedule :date class="no-select"
                            :data-copy-cell="copyCellKey(schedule.id, date)"
                            :class="[copyHighlightClass(schedule.id, date), {
                                '!bg-yellow': isSourceCell(schedule, date),
                                '!bg-lightred': isTargetCell(schedule, date),
                            }]"
                            @click="onDayClick($event, date, schedule)" />
                    </template>
                </div>
            </div>
        </template>
    </div>

    <ShiftCopyModal
        v-if="copyProcess.isModalOpen.value"
        :mode="copyProcess.modalMode.value"
        :errors="copyProcess.modalErrors.value"
        :preview="copyProcess.preview.value"
        :is-loading="copyProcess.isLoading.value"
        @close="copyProcess.close"
        @confirm="copyProcess.confirm"
    />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import MonthScheduleItem from './MonthScheduleItem.vue'
import WeekScheduleItem from './WeekScheduleItem.vue'
import Employee from '../employee/Employee.vue'
import dayjs from 'dayjs'
import type { Schedule } from '@/types'
import Loader from '@/ui/Loader.vue'
import ShiftCopyModal from './ShiftCopyModal.vue'
import { useToast } from 'vue-toast-notification'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import { useShiftCopy } from './useShiftCopy'
import { clearCopyHighlight, copyCellKey, copyHighlightClass, useCopyHighlightSurface } from './copyHighlight'
import { bulkDeleteShifts } from '@/api/schedule'
import { isOtherDepartmentShift, ownRowShifts } from '@/utils/otherDepartmentShifts'

interface Props {
    days: number
    today: string
    schedules: Schedule[]
    // Точные даты столбцов: Пн–Вс недели или все дни месяца.
    dates?: string[]
    // Ячейка месячного (RD-138) или недельного (RD-137) графика.
    layout?: 'week' | 'month'
}

const scheduleStore = useScheduleStore()
const departmentStore = useDepartmentStore()
const authStore = useAuthStore()
const toast = useToast({ position: 'bottom-right' })
const copyProcess = useShiftCopy({ backgroundRefresh: true })
const tableRef = ref<HTMLElement | null>(null)
const sourceDates = ref<string[]>([])
const targetDates = ref<string[]>([])
const selectedEmploymentId = ref<string | null>(null)
const sourceIsFixed = ref(false)
const isDeleting = ref(false)

const { schedules, dates, layout = 'week' } = defineProps<Props>()
const emit = defineEmits(['day-clicked', 'user-clicked'])

const isWarnedEmployee = (id: string) => departmentStore.warnedEmployee.includes(id)
const getHoursLimitViolation = (id: string) => departmentStore.hoursLimitViolations.find(item => item.employment_id === id)

const resetCopy = () => {
    sourceDates.value = []
    targetDates.value = []
    selectedEmploymentId.value = null
    sourceIsFixed.value = false
}

// Операции работают с точной датой ячейки: в неделе даты могут относиться к двум месяцам.
// Смены других подразделений (RD-175/RD-176) только отображаются: в выделение, редактирование,
// удаление и копирование попадают собственные смены строки — её ТУ в выбранном подразделении (RD-182).
const getOwnShifts = (schedule: Schedule) => ownRowShifts(schedule, scheduleStore.currentDepartment?.id)
const getDayShifts = (schedule: Schedule, cellDate: string) => getOwnShifts(schedule).filter(shift => dayjs(shift.date).isSame(cellDate, 'day'))
const hasOtherDepartmentDayShifts = (schedule: Schedule, cellDate: string) => schedule.shifts.some(shift => (
    dayjs(shift.date).isSame(cellDate, 'day') && isOtherDepartmentShift(shift, scheduleStore.currentDepartment?.id)
))
const canSelectCell = (schedule: Schedule, cellDate: string) => {
    const date = dayjs(cellDate)
    if (!date.isAfter(dayjs(), 'day')) {
        toast.error('Для выделения доступны только будущие даты')
        return false
    }
    if (date.isBefore(dayjs(schedule.hired_at), 'day') || date.isAfter(dayjs(schedule.fired_at), 'day')) {
        toast.error('Дата находится вне периода трудоустройства')
        return false
    }
    const shifts = getDayShifts(schedule, cellDate)
    if (shifts.some(shift => Boolean(shift.day_state_document))) {
        toast.error('День с кадровым отклонением недоступен для выделения')
        return false
    }
    if (shifts.some(shift => Boolean(shift.is_vacancy))) {
        toast.error('Смена из биржи недоступна для выделения')
        return false
    }
    const hasNonEditableDayType = shifts.some(shift => (
        scheduleStore.dayTypes.find(item => item.id === shift.day_type)?.is_not_editable_in_schedule
    ))
    if (hasNonEditableDayType) {
        toast.error('Этот тип дня недоступен для выделения')
        return false
    }
    return true
}

const isSourceCell = (schedule: Schedule, cellDate: string) => selectedEmploymentId.value === schedule.id && sourceDates.value.includes(cellDate)
const isTargetCell = (schedule: Schedule, cellDate: string) => selectedEmploymentId.value === schedule.id && targetDates.value.includes(cellDate)

const toggleDate = (dates: string[], date: string) => dates.includes(date)
    ? dates.filter(item => item !== date)
    : [...dates, date]

const onDayClick = (event: MouseEvent, cellDate: string, schedule: Schedule) => {
    tableRef.value?.focus()
    if (copyProcess.isLoading.value || isDeleting.value) return
    if (event.shiftKey) {
        if (!authStore.permissions?.can_create_schedule) {
            toast.error('У вас нет прав на данное действие')
            return
        }
        if (!canSelectCell(schedule, cellDate)) return

        if (selectedEmploymentId.value && selectedEmploymentId.value !== schedule.id) resetCopy()
        selectedEmploymentId.value = schedule.id
        const date = cellDate
        if (sourceIsFixed.value) {
            if (sourceDates.value.includes(date)) {
                toast.error('Дата источника не может быть датой назначения')
                return
            }
            targetDates.value = toggleDate(targetDates.value, date)
        } else {
            sourceDates.value = toggleDate(sourceDates.value, date)
        }
        return
    }

    resetCopy()
    if (dayjs(schedule.hired_at).isAfter(dayjs(cellDate), 'day') || dayjs(schedule.fired_at).isBefore(dayjs(cellDate), 'day')) return
    const shifts = getDayShifts(schedule, cellDate)
    const dayType = shifts[0] && scheduleStore.dayTypes.find(item => item.id === shifts[0].day_type)
    if (dayType?.is_not_editable_in_schedule) return
    const payload = { day: dayjs(cellDate).date(), date: cellDate, index: scheduleStore.schedules.indexOf(schedule), cell: event.currentTarget }
    if (schedule.department.id === scheduleStore.currentDepartment?.id && !schedule.is_vacancy) {
        // День только со сменами в другом подразделении по-прежнему не открывается для редактирования.
        if (shifts.length || !hasOtherDepartmentDayShifts(schedule, cellDate)) emit('day-clicked', payload)
    } else if (shifts.length) {
        emit('day-clicked', payload)
    }
}

const isEditableTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
}

interface BulkDeleteErrorResponse {
    msg?: string
}

const getBulkDeleteErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object' || !('response' in error)) return undefined

    const response = error.response
    if (!response || typeof response !== 'object' || !('data' in response)) return undefined

    const data = response.data as BulkDeleteErrorResponse
    return data.msg
}

const deleteSelectedShifts = async () => {
    if (isDeleting.value || !selectedEmploymentId.value || !sourceDates.value.length) return

    const schedule = schedules.find(item => item.id === selectedEmploymentId.value)
    if (!schedule) return

    const shiftIds = [...new Set(sourceDates.value.flatMap(date => (
        getOwnShifts(schedule)
            .filter(shift => dayjs(shift.date).isSame(date, 'day'))
            .map(shift => shift.id)
            .filter((id): id is string => Boolean(id))
    )))]
    if (!shiftIds.length) {
        toast.error('Нет смен для удаления')
        return
    }

    isDeleting.value = true
    try {
        const response = await bulkDeleteShifts({ shift_ids: shiftIds })
        toast.success(response.data.msg)
        await scheduleStore.fetchSchedule({ background: true })
        resetCopy()
    } catch (error: unknown) {
        toast.error(getBulkDeleteErrorMessage(error) ?? 'Не удалось удалить смены')
    } finally {
        isDeleting.value = false
    }
}

const handleKeyDown = async (event: KeyboardEvent) => {
    if (isEditableTarget(event.target) || copyProcess.isLoading.value || isDeleting.value) return
    if (!authStore.permissions?.can_create_schedule) return
    const withModifier = event.ctrlKey || event.metaKey

    if (event.code === 'Escape' && (sourceDates.value.length || targetDates.value.length)) {
        event.preventDefault()
        resetCopy()
        return
    }

    if (event.code === 'Delete' && sourceDates.value.length && selectedEmploymentId.value) {
        event.preventDefault()
        await deleteSelectedShifts()
        return
    }

    if (withModifier && event.code === 'KeyC' && sourceDates.value.length && selectedEmploymentId.value) {
        event.preventDefault()
        const schedule = schedules.find(item => item.id === selectedEmploymentId.value)
        if (!schedule) return
        const hasSourceShifts = sourceDates.value.some(date => (
            getOwnShifts(schedule).some(shift => dayjs(shift.date).isSame(date, 'day'))
        ))
        if (!hasSourceShifts) {
            toast.error('Нет смен для копирования')
            resetCopy()
            return
        }
        sourceDates.value = [...sourceDates.value].sort()
        sourceIsFixed.value = true
        const copiedShifts = getOwnShifts(schedule).filter(shift => sourceDates.value.includes(shift.date ?? '') && !shift.is_vacancy).length
        toast.success(`Выбрано ячеек: ${sourceDates.value.length}, копируемых смен: ${copiedShifts}`)
        return
    }

    if (withModifier && event.code === 'KeyV' && sourceIsFixed.value) {
        event.preventDefault()
        if (!targetDates.value.length || !selectedEmploymentId.value || !scheduleStore.currentDepartment?.id) {
            toast.error('Выберите даты назначения')
            return
        }
        await copyProcess.start({
            department_id: scheduleStore.currentDepartment.id,
            employment_ids: [selectedEmploymentId.value],
            source_dates: [...sourceDates.value].sort(),
            target_dates: [...targetDates.value].sort(),
        }, resetCopy)
    }

    if (withModifier && event.code === 'KeyX' && (sourceDates.value.length || targetDates.value.length)) {
        event.preventDefault()
        resetCopy()
    }
}

watch(() => scheduleStore.currentScheduleDate.valueOf(), resetCopy)
watch(() => scheduleStore.currentWeekStart.valueOf(), resetCopy)
watch(() => scheduleStore.currentDepartment?.id, resetCopy)
// Подсветка копирования из редактора (RD-140) относится только к текущему контексту сетки.
watch([
    () => scheduleStore.currentScheduleDate.valueOf(),
    () => scheduleStore.currentWeekStart.valueOf(),
    () => scheduleStore.currentDepartment?.id,
], clearCopyHighlight)
useCopyHighlightSurface()
</script>

<style scoped>
.schedule-keyboard-surface:focus {
    outline: none;
}

.no-select {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

</style>
